import React, { useState, useEffect, useCallback } from 'react'
import { CheckCircle2, XCircle, Phone, CreditCard, UserCheck, Scissors } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { fetchAnalytics } from '../../../../api/analyticsClient'
import KPICard from '../components/KPICard'
import AnalyticsCard from '../components/AnalyticsCard'
import styles from './SupplySection.module.css'

const fmt = (n) => {
  if (n === null || n === undefined) return '—'
  const num = Number(n)
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toLocaleString('en-IN')
}
const fmtPct = (n) => (n !== null && n !== undefined) ? parseFloat(n).toFixed(1) + '%' : '—'
const fmtCurrency = (n) => {
  if (n === null || n === undefined) return '—'
  const num = Number(n)
  if (num >= 100000) return '₹' + (num / 100000).toFixed(1) + 'L'
  if (num >= 1000) return '₹' + (num / 1000).toFixed(1) + 'K'
  return '₹' + num.toLocaleString('en-IN')
}
const initState = () => ({ data: null, loading: true, error: null })

const SupplySection = () => {
  const [profileCompletion, setProfileCompletion] = useState(initState)
  const [salaryDist, setSalaryDist] = useState(initState)
  const [workDomains, setWorkDomains] = useState(initState)
  const [verificationRates, setVerificationRates] = useState(initState)

  const loadAll = useCallback(async () => {
    setProfileCompletion(s => ({ ...s, loading: true, error: null }))
    setSalaryDist(s => ({ ...s, loading: true, error: null }))
    setWorkDomains(s => ({ ...s, loading: true, error: null }))
    setVerificationRates(s => ({ ...s, loading: true, error: null }))

    const [profileRes, salaryRes, domainsRes, verifyRes] = await Promise.all([
      fetchAnalytics('/analytics/supply/profile-completion'),
      fetchAnalytics('/analytics/supply/salary-distribution', { bucket_size: 5000 }),
      fetchAnalytics('/analytics/supply/work-domains', { limit: 15 }),
      fetchAnalytics('/analytics/supply/verification-rates'),
    ])

    setProfileCompletion({ data: profileRes.data, loading: false, error: profileRes.error })
    setSalaryDist({ data: salaryRes.data, loading: false, error: salaryRes.error })
    setWorkDomains({ data: domainsRes.data, loading: false, error: domainsRes.error })
    setVerificationRates({ data: verifyRes.data, loading: false, error: verifyRes.error })
  }, [])

  useEffect(() => { loadAll() }, [loadAll])

  const completionRate = profileCompletion.data?.completion_rate_pct || 0
  const circumference = 2 * Math.PI * 44
  const strokeDash = circumference * (completionRate / 100)

  const salaryHistogram = salaryDist.data?.histogram?.map(b => ({
    bucket: `₹${b.bucket_floor >= 1000 ? (b.bucket_floor / 1000).toFixed(0) + 'K' : b.bucket_floor}–${b.bucket_ceil >= 1000 ? (b.bucket_ceil / 1000).toFixed(0) + 'K' : b.bucket_ceil}`,
    count: b.count,
  })) || []

  return (
    <div className={styles.section}>
      {/* Profile completion + Verification rates */}
      <div className={styles.topGrid}>
        <AnalyticsCard
          title="Profile Completion"
          subtitle="Stylists who have completed their profile"
          loading={profileCompletion.loading}
          error={profileCompletion.error}
        >
          {profileCompletion.data && (
            <div className={styles.completionContent}>
              <div className={styles.ringWrapper}>
                <svg viewBox="0 0 100 100" className={styles.ring}>
                  <circle cx="50" cy="50" r="44" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                  <circle
                    cx="50"
                    cy="50"
                    r="44"
                    fill="none"
                    stroke={completionRate >= 70 ? '#16a34a' : completionRate >= 40 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="10"
                    strokeDasharray={`${strokeDash} ${circumference}`}
                    strokeDashoffset={circumference * 0.25}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dasharray 0.6s ease' }}
                  />
                  <text x="50" y="46" textAnchor="middle" className={styles.ringPct}>{fmtPct(completionRate)}</text>
                  <text x="50" y="60" textAnchor="middle" className={styles.ringLabel}>complete</text>
                </svg>
              </div>
              <div className={styles.completionStats}>
                <div className={styles.completionStat}>
                  <CheckCircle2 size={16} className={styles.iconGreen} />
                  <div>
                    <div className={styles.completionStatValue}>{fmt(profileCompletion.data.profiles_completed)}</div>
                    <div className={styles.completionStatLabel}>Completed</div>
                  </div>
                </div>
                <div className={styles.completionStat}>
                  <XCircle size={16} className={styles.iconRed} />
                  <div>
                    <div className={styles.completionStatValue}>{fmt(profileCompletion.data.profiles_incomplete)}</div>
                    <div className={styles.completionStatLabel}>Incomplete</div>
                  </div>
                </div>
                <div className={styles.completionTotal}>
                  <span className={styles.completionTotalValue}>{fmt(profileCompletion.data.total_stylists)}</span>
                  <span className={styles.completionTotalLabel}>total stylists</span>
                </div>
              </div>
            </div>
          )}
        </AnalyticsCard>

        <div className={styles.verifyCards}>
          <div className={styles.verifyCardTitle}>Verification Rates</div>
          <KPICard
            label="PAN Verified"
            value={fmtPct(verificationRates.data?.pan_rate_pct)}
            icon={CreditCard}
            status={verificationRates.data?.pan_rate_pct >= 50 ? 'green' : verificationRates.data?.pan_rate_pct >= 25 ? 'amber' : 'red'}
            subtext={verificationRates.data ? `${fmt(verificationRates.data.pan_verified)} stylists` : undefined}
            loading={verificationRates.loading}
          />
          <KPICard
            label="WhatsApp Linked"
            value={fmtPct(verificationRates.data?.whatsapp_rate_pct)}
            icon={Phone}
            status={verificationRates.data?.whatsapp_rate_pct >= 60 ? 'green' : verificationRates.data?.whatsapp_rate_pct >= 30 ? 'amber' : 'red'}
            subtext={verificationRates.data ? `${fmt(verificationRates.data.whatsapp_linked)} stylists` : undefined}
            loading={verificationRates.loading}
          />
          <KPICard
            label="Active Profiles"
            value={fmtPct(verificationRates.data?.active_rate_pct)}
            icon={UserCheck}
            status={verificationRates.data?.active_rate_pct >= 70 ? 'green' : verificationRates.data?.active_rate_pct >= 40 ? 'amber' : 'red'}
            subtext={verificationRates.data ? `${fmt(verificationRates.data.active_profiles)} of ${fmt(verificationRates.data.total)} total` : undefined}
            loading={verificationRates.loading}
          />
          {!verificationRates.loading && verificationRates.data && (
            <div className={styles.deletedNote}>
              <XCircle size={12} className={styles.iconRed} />
              {fmt(verificationRates.data.deleted_profiles)} deleted profiles excluded from rates
            </div>
          )}
        </div>
      </div>

      {/* Salary Distribution */}
      <AnalyticsCard
        title="Salary Distribution"
        subtitle={salaryDist.data ? `Avg: ${fmtCurrency(salaryDist.data.summary?.avg_salary)} · Range: ${fmtCurrency(salaryDist.data.summary?.min_salary)}–${fmtCurrency(salaryDist.data.summary?.max_salary)} · ${fmt(salaryDist.data.summary?.zero_salary_count)} with no salary set` : 'Expected salary histogram'}
        loading={salaryDist.loading}
        error={salaryDist.error}
      >
        {salaryHistogram.length > 0 && (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={salaryHistogram} margin={{ top: 4, right: 16, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="bucket"
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
                angle={-35}
                textAnchor="end"
                height={50}
              />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt} />
              <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }} formatter={v => [fmt(v), 'Stylists']} />
              <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </AnalyticsCard>

      {/* Work Domains */}
      <div className={styles.domainGrid}>
        <AnalyticsCard
          title="Primary Work Domains"
          subtitle="Stylists' primary specialization areas"
          loading={workDomains.loading}
          error={workDomains.error}
        >
          {workDomains.data?.primary_domains?.length > 0 && (
            <ResponsiveContainer width="100%" height={Math.min(360, workDomains.data.primary_domains.length * 28 + 40)}>
              <BarChart data={workDomains.data.primary_domains} layout="vertical" margin={{ top: 0, right: 16, left: 90, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt} />
                <YAxis type="category" dataKey="domain" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={90} />
                <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }} formatter={v => [fmt(v), 'Stylists']} />
                <Bar dataKey="stylists" fill="#2563eb" radius={[0, 4, 4, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title="Secondary Work Domains"
          subtitle="Stylists' secondary specialization areas"
          loading={workDomains.loading}
          error={workDomains.error}
        >
          {workDomains.data?.secondary_domains?.length > 0 && (
            <ResponsiveContainer width="100%" height={Math.min(360, workDomains.data.secondary_domains.length * 28 + 40)}>
              <BarChart data={workDomains.data.secondary_domains} layout="vertical" margin={{ top: 0, right: 16, left: 90, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt} />
                <YAxis type="category" dataKey="domain" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={90} />
                <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }} formatter={v => [fmt(v), 'Stylists']} />
                <Bar dataKey="stylists" fill="#7c3aed" radius={[0, 4, 4, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </AnalyticsCard>
      </div>
    </div>
  )
}

export default SupplySection
